import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "@tanstack/react-router";
import { Building2, Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { useSession } from "@/auth/session";
import { useI18n } from "@/i18n/provider";
import type { TKey } from "@/i18n/dict";
import type { OrganizationRole } from "@/api/types";
import { cn } from "@/lib/utils";

const ROLE_KEY: Record<OrganizationRole, TKey> = {
  owner: "role.owner",
  admin: "role.admin",
  operator: "role.operator",
  viewer: "role.viewer",
};

/**
 * The organization switcher.
 *
 * A consultant managing three banks lives in this control, so it sits in the header as a
 * first-class thing, not buried three clicks deep in settings. It shows the role in each
 * org, because the same person is an owner of one and a viewer of another, and knowing
 * which before you click is the difference between confidence and a 403.
 *
 * Switching is a SERVER operation: the session row holds the active organization, so the
 * client asks the server to move, then re-reads /v1/me. The client never simply "decides"
 * which org it is in — that would be a tenancy boundary living in a React state variable.
 */
export function OrgSwitcher() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { activeOrg, memberships, switchOrg, isSwitching } = useSession();

  if (!activeOrg) return null;

  const others = memberships.filter((m) => m.organization.id !== activeOrg.id);
  const role = memberships.find((m) => m.organization.id === activeOrg.id)?.role;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        disabled={isSwitching}
        aria-label={t("org.switcher.switch")}
        className={cn(
          "on-chrome flex min-w-0 items-center gap-2 rounded-[6px] px-2.5 py-1.5",
          "border border-chrome-rule text-start transition-colors",
          "hover:bg-panel focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:opacity-60",
        )}
      >
        {isSwitching ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-chrome-muted" aria-hidden />
        ) : (
          <Building2 className="size-4 shrink-0 text-chrome-muted" aria-hidden />
        )}

        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium text-chrome-text">
            {activeOrg.name}
          </span>
          {role ? (
            <span className="truncate text-[10px] text-chrome-muted">{t(ROLE_KEY[role])}</span>
          ) : null}
        </span>

        <ChevronsUpDown className="size-3.5 shrink-0 text-chrome-muted" aria-hidden />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 min-w-64 overflow-hidden rounded-[6px] border border-rule-strong bg-paper p-1 shadow-[0_12px_36px_rgba(57,54,70,0.2)]"
        >
          <DropdownMenu.Label className="label-caps px-2 py-1.5 font-mono text-[10px] text-faint">
            {t("org.switcher.label")}
          </DropdownMenu.Label>

          <DropdownMenu.Item
            disabled
            className="flex items-center gap-2 rounded-[4px] px-2 py-2 text-sm text-text opacity-100"
          >
            <Check className="size-4 shrink-0 text-ink" aria-hidden />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{activeOrg.name}</span>
              <span className="truncate font-mono text-[11px] text-faint">{activeOrg.slug}</span>
            </span>
          </DropdownMenu.Item>

          {others.length > 0 ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-rule" />
              {others.map((membership) => (
                <DropdownMenu.Item
                  key={membership.organization.id}
                  onSelect={() => {
                    void switchOrg(membership.organization.id).then(() => {
                      // Land on the dashboard: a site id from the previous org means
                      // nothing in this one, and would render a 404 the moment we arrive.
                      void navigate({ to: "/" });
                    });
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-2 text-sm text-text outline-none data-[highlighted]:bg-paper-sunk"
                >
                  <span className="size-4 shrink-0" aria-hidden />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{membership.organization.name}</span>
                    <span className="truncate font-mono text-[11px] text-faint">
                      {membership.organization.slug}
                    </span>
                  </span>
                  <span className="shrink-0 text-[11px] text-faint">
                    {t(ROLE_KEY[membership.role])}
                  </span>
                </DropdownMenu.Item>
              ))}
            </>
          ) : null}

          <DropdownMenu.Separator className="my-1 h-px bg-rule" />

          <DropdownMenu.Item
            onSelect={() => void navigate({ to: "/onboarding/organization" })}
            className="flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-2 text-sm text-text outline-none data-[highlighted]:bg-paper-sunk"
          >
            <Plus className="size-4 shrink-0 text-muted" aria-hidden />
            {t("org.switcher.create")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
